// Sync API client. Every call carries the Cognito ID token as a bearer token;
// the HTTP API's JWT authorizer validates it and the Lambda scopes data to the
// caller's `sub`. All methods reject if called while signed out.
import { syncConfig } from '../config';
import { idToken } from '../auth/cognito';
import type { Pose, Routine, RoutineStep } from '../types';
import type { ProgressStatus } from '../types';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!syncConfig) throw new Error('Sync is not configured.');
  const token = await idToken();
  if (!token) throw new Error('Not signed in.');

  const res = await fetch(`${syncConfig.apiUrl}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new Error(`API ${method} ${path} failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Unauthenticated read of a routine shared by link. Null when not found. */
async function getPublicRoutine(id: string): Promise<Routine | null> {
  if (!syncConfig) return null;
  const res = await fetch(`${syncConfig.apiUrl}/public/routines/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Public routine fetch failed (${res.status}).`);
  return ((await res.json()) as { routine: Routine }).routine;
}

/** Unauthenticated list of all shared routines (the public gallery). */
async function listPublicRoutines(): Promise<Routine[]> {
  if (!syncConfig) return [];
  const res = await fetch(`${syncConfig.apiUrl}/public/routines`);
  if (!res.ok) throw new Error(`Public gallery fetch failed (${res.status}).`);
  return ((await res.json()) as { routines: Routine[] }).routines;
}

/** Unauthenticated pose-image override manifest: poseId → image URL. */
async function listPoseImageOverrides(): Promise<Record<string, string>> {
  if (!syncConfig) return {};
  const res = await fetch(`${syncConfig.apiUrl}/public/pose-images`);
  if (!res.ok) return {};
  return ((await res.json()) as { overrides: Record<string, string> }).overrides ?? {};
}

/** Unauthenticated list of admin-created runtime poses (merged with the seed). */
async function listPoses(): Promise<Pose[]> {
  if (!syncConfig) return [];
  const res = await fetch(`${syncConfig.apiUrl}/public/poses`);
  if (!res.ok) return [];
  return ((await res.json()) as { poses: Pose[] }).poses ?? [];
}

/** AI-drafted pose fields (admin reviews these before saving). */
export interface PoseDraft {
  nameEnglish: string;
  nameSanskrit: string;
  category: string;
  difficultyTier: number;
  description: string;
  cues: string[];
  benefits: string[];
  hold: string;
  modification: string;
}

/** A pose as the routine generator needs to see it (the client's catalog). */
export interface GenPoseInput {
  id: string;
  name: string;
  tier: number;
  category: string;
  bilateral: boolean;
}

/** Criteria the user picks in the "Generate a routine" form. */
export interface GenerateRoutineInput {
  name: string;
  purpose: string;
  durationMinutes: number;
  maxLevel: number;
  poses: GenPoseInput[];
}

/** An AI-generated routine draft (reviewed/edited before it's saved). */
export interface GeneratedRoutine {
  name: string;
  note: string;
  steps: RoutineStep[];
}

export type FeedbackImportance = 'Low' | 'Nice-to-have' | 'Important';

/** A user's "Suggest an improvement" request. */
export interface FeedbackInput {
  summary: string;
  details: string;
  importance: FeedbackImportance;
  /** Auto-captured context, e.g. "Pose: Tree Pose (tree-pose)" or the page. */
  context?: string;
  /** Optional images as downscaled JPEG data URLs. */
  images?: string[];
}

export type FeedbackStatus = 'new' | 'reviewed' | 'done';

/** A stored improvement request, as the admin review page sees it. */
export interface FeedbackItem {
  id: string;
  createdAt: string;
  summary: string;
  details: string;
  importance: FeedbackImportance;
  context: string;
  submitterEmail: string;
  status: FeedbackStatus;
  imageUrls: string[];
}

export interface ImageJob {
  status: string; // pending | done | error | unknown
  candidateUrl: string | null;
  candidateKey: string | null;
  message: string | null;
}

/** Fields the image generator uses to build its prompt. */
export interface ImagePosePrompt {
  nameEnglish: string;
  description?: string;
  cues?: string[];
}

export const api = {
  getPublicRoutine,
  listPublicRoutines,
  listPoseImageOverrides,
  listPoses,

  // --- admin: pose-image editing (requires the admins group) ---
  createImageJob: (
    poseId: string,
    feedback: string,
    pose: ImagePosePrompt,
    referenceImage?: string, // optional data-URL/base64 photo of the target position
  ) =>
    request<{ jobId: string }>('POST', `/admin/poses/${encodeURIComponent(poseId)}/image-jobs`, {
      feedback,
      pose,
      ...(referenceImage ? { referenceImage } : {}),
    }),
  getImageJob: (poseId: string, jobId: string) =>
    request<ImageJob>(
      'GET',
      `/admin/poses/${encodeURIComponent(poseId)}/image-jobs/${encodeURIComponent(jobId)}`,
    ),
  approvePoseImage: (poseId: string, key: string) =>
    request<{ poseId: string; url: string }>('POST', `/admin/poses/${encodeURIComponent(poseId)}/image`, {
      key,
    }),
  revertPoseImage: (poseId: string) =>
    request<void>('DELETE', `/admin/poses/${encodeURIComponent(poseId)}/image`),

  // --- admin: add / draft / delete runtime poses ---
  draftPose: (name: string) =>
    request<{ draft: PoseDraft }>('POST', '/admin/poses/draft', { name }).then((r) => r.draft),
  savePose: (pose: Partial<Pose> & { nameEnglish: string }) =>
    request<{ pose: Pose }>('POST', '/admin/poses', pose).then((r) => r.pose),
  deletePose: (id: string) => request<void>('DELETE', `/admin/poses/${encodeURIComponent(id)}`),
  // --- routines: AI generation (any signed-in user) ---
  generateRoutine: (input: GenerateRoutineInput) =>
    request<{ routine: GeneratedRoutine }>('POST', '/routines/generate', input).then(
      (r) => r.routine,
    ),
  // --- feedback: "Suggest an improvement" (any signed-in user) ---
  submitFeedback: (input: FeedbackInput) =>
    request<{ id: string }>('POST', '/feedback', input).then((r) => r.id),

  // --- admin: review feedback (requires the admins group) ---
  listFeedback: () =>
    request<{ items: FeedbackItem[] }>('GET', '/admin/feedback').then((r) => r.items),
  setFeedbackStatus: (id: string, createdAt: string, status: FeedbackStatus) =>
    request<{ id: string; status: FeedbackStatus }>(
      'POST',
      `/admin/feedback/${encodeURIComponent(id)}`,
      { createdAt, status },
    ),
  deleteFeedback: (id: string, createdAt: string) =>
    request<void>(
      'DELETE',
      `/admin/feedback/${encodeURIComponent(id)}?createdAt=${encodeURIComponent(createdAt)}`,
    ),

  listRoutines: () => request<{ routines: Routine[] }>('GET', '/routines').then((r) => r.routines),
  putRoutine: (routine: Routine) =>
    request<Routine>('PUT', `/routines/${encodeURIComponent(routine.id)}`, routine),
  deleteRoutine: (id: string) => request<void>('DELETE', `/routines/${encodeURIComponent(id)}`),
  getProgress: () =>
    request<{ map: Record<string, ProgressStatus> }>('GET', '/progress').then((r) => r.map),
  putProgress: (map: Record<string, ProgressStatus>) =>
    request<{ map: Record<string, ProgressStatus> }>('PUT', '/progress', { map }).then((r) => r.map),
};
